import React, { PropsWithChildren } from "react";

import { connect } from "react-redux";
import * as SessionActions from "../actions/sessionActions";

type sessionShape = {
  authenticated?: boolean;
  subscriptionStorageQuota?: number;
  activeSubscription?: boolean;
  refreshSession?: () => Promise<sessionShape>;
  deleteSession?: () => void;
  subscriptionType?: string;
  token?: string;
  applicationSpecifics?: $TSFixMe;
  sessionDeleted?: boolean;
  tokenExpiresAt?: number;
  myEndressSsoLogoutUrl?: string;
  reseller?: {
    customer?: {
      email?: string;
      first_name?: string;
      last_name?: string;
    };
  };
};

type WithSessionProps = {
  session: sessionShape;
};

const SessionContext = React.createContext<WithSessionProps | null>(null);

type SessionContextProviderProps = {
  dispatch: $TSFixMeFunction;
  deprecatedSession: sessionShape;
  testSession?: $TSFixMe;
};

function SessionContextProvider({
  children,
  dispatch,
  deprecatedSession,
  testSession = {},
}: Readonly<PropsWithChildren<SessionContextProviderProps>>) {
  const refreshSession = () =>
    new Promise((resolve, reject) => {
      dispatch(SessionActions.refreshSession())
        // eslint-disable-next-line no-use-before-define
        .then(() => resolve(session()))
        .catch(reject);
    });

  const deleteSession = () => dispatch(SessionActions.deleteSession());

  function session() {
    return {
      authenticated: deprecatedSession.authenticated,
      activeSubscription: deprecatedSession.activeSubscription,
      subscriptionStorageQuota: deprecatedSession.subscriptionStorageQuota,
      subscriptionType: deprecatedSession.subscriptionType,
      token: deprecatedSession.token,
      applicationSpecifics: deprecatedSession.applicationSpecifics,
      reseller: deprecatedSession.reseller,
      tokenExpiresAt: deprecatedSession.tokenExpiresAt,
      myEndressSsoLogoutUrl: deprecatedSession.myEndressSsoLogoutUrl,
      refreshSession,
      deleteSession,
      ...testSession,
    };
  }

  const value = React.useMemo(
    () => ({ session: session() }),
    [deprecatedSession, testSession],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

const ConnectedSessionContextProvider = connect((state) => ({
  deprecatedSession: (state as $TSFixMe).session,
}))(SessionContextProvider);

const withSession = <T extends WithSessionProps = WithSessionProps>(
  ChildComponent: React.ComponentType<T>,
) => {
  function ConnectedComponent(
    props: Readonly<Omit<T, keyof WithSessionProps>>,
  ) {
    return (
      <SessionContext.Consumer>
        {(context) => {
          // istanbul ignore next
          if (!context) {
            throw new Error(
              "withSession must be used inside a SessionContextProvider",
            );
          }
          return <ChildComponent {...(props as T)} {...context} />;
        }}
      </SessionContext.Consumer>
    );
  }
  ConnectedComponent.displayName =
    ChildComponent.displayName ?? ChildComponent.name;
  return ConnectedComponent;
};

const useSession = () => {
  const context = React.useContext(SessionContext);
  // istanbul ignore next
  if (!context) {
    throw new Error("useSession must be used inside a SessionContextProvider");
  }
  return context.session;
};

export {
  // sessionShape #FIXME: first we need to kill the old sessionShape
  ConnectedSessionContextProvider as SessionContextProvider,
  withSession,
  useSession,
};
